import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  readonly children: ReactNode;
  /** Re-mounts the boundary (clearing the caught error) when this changes — pass the route. */
  readonly resetKey: string;
}

interface State {
  readonly failed: boolean;
}

/**
 * The one place a screen is allowed to fail. Without it a render error in
 * any screen unmounts the whole React tree and the learner is left with a
 * blank page and no way back (the debrief did exactly this on 2026-09-08).
 * The message is deliberately plain and non-mechanical; the error itself goes
 * to the console, which is where a developer will look for it.
 */
export class ScreenErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("screen failed to render", error, info.componentStack);
  }

  override componentDidUpdate(previous: Props): void {
    if (previous.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false });
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <main className="min-h-[calc(100svh-6rem)] bg-paper px-gutter pt-10 pb-16">
          <div className="mx-auto font-sans text-body" style={{ width: "var(--pc-measure)" }}>
            <p className="m-0">This page could not be drawn. Nothing you did has been lost.</p>
            <p className="mt-4 mb-0">
              <a href="#/play" className="underline">
                back to the engagement.
              </a>
            </p>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
